import React from 'react';

import Input from '../FormElements/Input';
import type {InputProps as NewInputProps} from '../FormElements/Input';
import type {InputProps as LegacyInputProps} from './LegacyInput';
import * as OldInputComponent from './LegacyInput';

/**
 * Create adapter layer to migrate LegacyInput to new Input component
 * inputs props are the old LegacyInput props
 * Component will transform the props to the new Input props
 * Note: This is a temporary measure to enable more gradual migration
 */

/**
 * These props existed in LegacyInput but not in the new Input
 */
type DeprecatedProps = Partial<{
  /** @deprecated */
  pattern: LegacyInputProps['pattern'];
  /** @deprecated */
  onHover: LegacyInputProps['onHover'];
  /** @deprecated */
  inputRef: LegacyInputProps['inputRef'];
  /** @deprecated */
  disabled: LegacyInputProps['disabled'];
  /** @deprecated */
  enableVisibilityToggle: LegacyInputProps['enableVisibilityToggle'];
  /** @deprecated */
  validationEmotes: LegacyInputProps['validationEmotes'];
  /** @deprecated */
  typeIcons: LegacyInputProps['typeIcons'];
  /** @deprecated */
  width: LegacyInputProps['width'];
  /** @deprecated */
  tabIndex: LegacyInputProps['tabIndex'];
  /** @deprecated */
  updateFocus: LegacyInputProps['updateFocus'];
  /** @deprecated */
  updateValidationStyle: LegacyInputProps['updateValidationStyle'];
  /** @deprecated */
  searchClearText: LegacyInputProps['searchClearText'];
  /** @deprecated */
  clearInputType: LegacyInputProps['clearInputType'];
  /** @deprecated */
  showValidationTextWhileFocused: LegacyInputProps['showValidationTextWhileFocused'];
  /** @deprecated */
  showCountTextContainer: LegacyInputProps['showCountTextContainer'];
  /** @deprecated */
  title: LegacyInputProps['title'];
  /** @deprecated */
  titleWeight: LegacyInputProps['titleWeight'];
  /** @deprecated */
  validationStyles: LegacyInputProps['validationStyles'];
}>;

/**
 * These props existed but have been removed
 */
type RemovedProps = Partial<{
  /** @deprecated */
  inputKey: never;
}>;

const deprecatedKeys = new Set<keyof DeprecatedProps>([
  'pattern',
  'onHover',
  'inputRef',
  'disabled',
  'enableVisibilityToggle',
  'validationEmotes',
  'typeIcons',
  'width',
  'tabIndex',
  'updateFocus',
  'updateValidationStyle',
  'searchClearText',
  'clearInputType',
  'showValidationTextWhileFocused',
  'showCountTextContainer',
  'title',
  'titleWeight',
  'validationStyles',
]);

const removedKeys = new Set<keyof RemovedProps>(['inputKey']);

type InvalidProps = DeprecatedProps & RemovedProps;

export type InputProps = InvalidProps &
  Omit<NewInputProps, keyof InvalidProps> & {useNewInputComponent?: boolean};

const mapValidationStyle = (
  validationStyles: DeprecatedProps['validationStyles']
): NewInputProps['validationStyle'] => {
  if (validationStyles === 'icon') {
    return 'in-label';
  }
  if (validationStyles === 'normal') {
    return 'default';
  }
  return undefined;
};

const LegacyInput = (props: InputProps) => {
  const useOldInput = !props.useNewInputComponent || false;

  if (useOldInput) {
    return <OldInputComponent.default {...(props as LegacyInputProps)} />;
  }

  type FilteredInputProps = NewInputProps & Partial<Record<string, unknown>>;

  const filteredProps: FilteredInputProps = Object.keys(props).reduce(
    (acc: FilteredInputProps, key: string) => {
      // check if key is of type keyof InputProps
      if (!(key in props)) {
        return acc;
      }

      if (
        deprecatedKeys.has(key as keyof DeprecatedProps) ||
        removedKeys.has(key as keyof RemovedProps)
      ) {
        return acc;
      }

      const value = props[key as keyof InputProps];

      switch (key) {
        case 'validationStyles': {
          acc['validationStyle'] = mapValidationStyle(
            value as DeprecatedProps['validationStyles']
          );
          break;
        }
        case 'typeIcons': {
          acc['showTypeIcon'] = value as DeprecatedProps['typeIcons'];
          break;
        }
        default: {
          acc[key] = value;
        }
      }
      return acc;
    },
    {} as NewInputProps
  );

  return <Input {...filteredProps} />;
};

export default LegacyInput;
